// Pure form logic for the rent escalation setting screen, kept out of the
// component so the ladder rules can be tested without rendering.
import { RentEscalationSetting } from '@/services/api/rentEscalationSetting';

export interface RentEscalationFormValues {
  lateShutdownDays: string;
  lateRepossessionDays: string;
  autoRepossessionEnabled: boolean;
  notes: string;
}

export type RentEscalationFormErrors = Partial<
  Record<'lateShutdownDays' | 'lateRepossessionDays', string>
>;

export const fromSetting = (
  setting: RentEscalationSetting
): RentEscalationFormValues => ({
  lateShutdownDays: String(setting.lateShutdownDays),
  lateRepossessionDays: String(setting.lateRepossessionDays),
  autoRepossessionEnabled: setting.autoRepossessionEnabled,
  notes: '',
});

const toInt = (value: string): number | null => {
  if (!/^\d+$/.test(value.trim())) return null;
  return Number(value);
};

/**
 * Mirrors the API's 422 rules so ops sees them before the round trip: WARNED
 * starts at day 0, so a shutdown threshold of 0 would switch a unit off on the
 * first missed day; and the repossession rung has to sit above the shutdown
 * rung or the ladder never reaches SHUTDOWN_PENDING at all.
 */
export const validateRentEscalationForm = (
  values: RentEscalationFormValues
): RentEscalationFormErrors => {
  const errors: RentEscalationFormErrors = {};
  const shutdown = toInt(values.lateShutdownDays);
  const repo = toInt(values.lateRepossessionDays);

  if (shutdown === null) {
    errors.lateShutdownDays = 'Isi angka hari (bilangan bulat).';
  } else if (shutdown < 1) {
    errors.lateShutdownDays = 'Minimal 1 hari.';
  }

  if (repo === null) {
    errors.lateRepossessionDays = 'Isi angka hari (bilangan bulat).';
  } else if (shutdown !== null && repo <= shutdown) {
    errors.lateRepossessionDays = 'Harus lebih besar dari hari shutdown.';
  }

  return errors;
};

export const toUpdateBody = (values: RentEscalationFormValues) => ({
  lateShutdownDays: Number(values.lateShutdownDays),
  lateRepossessionDays: Number(values.lateRepossessionDays),
  autoRepossessionEnabled: values.autoRepossessionEnabled,
  notes: values.notes.trim() || undefined,
});

/**
 * True when saving would write something: a changed threshold or toggle, or a
 * note — a note-only save is a legitimate history entry ("kenapa tidak
 * diubah"), so it must not leave the button disabled.
 */
export const isDirty = (
  values: RentEscalationFormValues,
  setting: RentEscalationSetting | null
): boolean => {
  if (!setting) return true;
  return (
    Number(values.lateShutdownDays) !== setting.lateShutdownDays ||
    Number(values.lateRepossessionDays) !== setting.lateRepossessionDays ||
    values.autoRepossessionEnabled !== setting.autoRepossessionEnabled ||
    values.notes.trim().length > 0
  );
};
